import React, { useState } from "react";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Checkbox from "@cloudscape-design/components/checkbox";
import Header from "@cloudscape-design/components/header";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";

/**
 * ExportOptionsModal - Modal for selecting sections and columns before export
 * 
 * @param {boolean} visible - Whether modal is visible
 * @param {function} onDismiss - Callback when modal is dismissed
 * @param {function} onExport - Callback when export is confirmed with selections
 * @param {string} format - Export format (pdf, docx, json)
 */
export const ExportOptionsModal = ({ visible, onDismiss, onExport, format }) => {
  console.log("🎨 ExportOptionsModal render - visible:", visible, "format:", format);
  
  // Section selections
  const [sections, setSections] = useState({
    architectureDiagram: true,
    assets: true,
    dataFlow: false,
    trustBoundary: false,
    threatSource: true,
    threatCatalog: true,
  });

  // Column selections (for Threat Catalog table)
  const [columns, setColumns] = useState({
    threatId: true,
    threatName: true,
    description: true,
    strideCategory: true,
    impact: true,
    likelihood: true,
    riskScore: true,
    iso27001: true,
    mitigations: true,
  });

  // Risk score filter selections
  const [riskScoreFilter, setRiskScoreFilter] = useState({
    High: true,
    'Medium/High': true,
    Medium: true,
    'Low/Medium': true,
    Low: true,
  });

  // Section labels
  const sectionLabels = {
    architectureDiagram: "Architecture Diagram",
    assets: "Assets",
    dataFlow: "Data Flow",
    trustBoundary: "Trust Boundary",
    threatSource: "Threat Source",
    threatCatalog: "Threat Catalog",
  };

  // Column labels
  const columnLabels = {
    threatId: "Threat ID",
    threatName: "Threat Name",
    description: "Description",
    strideCategory: "STRIDE Category",
    impact: "Impact",
    likelihood: "Likelihood",
    riskScore: "Risk Score",
    iso27001: "ISO27001 Controls",
    mitigations: "Mitigations",
  };

  // Toggle section
  const toggleSection = (key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle column
  const toggleColumn = (key) => {
    setColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle risk score filter
  const toggleRiskScore = (key) => {
    setRiskScoreFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Select/Deselect all sections
  const selectAllSections = () => {
    const newState = {};
    Object.keys(sections).forEach((key) => {
      newState[key] = true;
    });
    setSections(newState);
  };

  const deselectAllSections = () => {
    const newState = {};
    Object.keys(sections).forEach((key) => {
      newState[key] = false;
    });
    setSections(newState);
  };

  // Select/Deselect all columns
  const selectAllColumns = () => {
    const newState = {};
    Object.keys(columns).forEach((key) => {
      newState[key] = true;
    });
    setColumns(newState);
  };

  const deselectAllColumns = () => {
    const newState = {};
    Object.keys(columns).forEach((key) => {
      newState[key] = false;
    });
    setColumns(newState);
  };

  // Select/Deselect all risk scores
  const selectAllRiskScores = () => {
    const newState = {};
    Object.keys(riskScoreFilter).forEach((key) => {
      newState[key] = true;
    });
    setRiskScoreFilter(newState);
  };

  const deselectAllRiskScores = () => {
    const newState = {};
    Object.keys(riskScoreFilter).forEach((key) => {
      newState[key] = false;
    });
    setRiskScoreFilter(newState);
  };

  // Handle export
  const handleExport = () => {
    onExport({ sections, columns, riskScoreFilter });
  };

  // Count selected items
  const selectedSectionsCount = Object.values(sections).filter(Boolean).length;
  const selectedColumnsCount = Object.values(columns).filter(Boolean).length;
  const selectedRiskScoresCount = Object.values(riskScoreFilter).filter(Boolean).length;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Export Options - ${format.toUpperCase()}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={selectedSectionsCount === 0}
            >
              Export {format.toUpperCase()}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="large"
    >
      <SpaceBetween size="l">
        {/* Sections Selection */}
        <Container
          header={
            <Header
              variant="h2"
              description={`${selectedSectionsCount} of ${Object.keys(sections).length} sections selected`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={selectAllSections} variant="inline-link">
                    Select All
                  </Button>
                  <Button onClick={deselectAllSections} variant="inline-link">
                    Deselect All
                  </Button>
                </SpaceBetween>
              }
            >
              Select Sections
            </Header>
          }
        >
          <ColumnLayout columns={2} variant="text-grid">
            {Object.entries(sectionLabels).map(([key, label]) => (
              <Checkbox
                key={key}
                checked={sections[key]}
                onChange={() => toggleSection(key)}
              >
                {label}
              </Checkbox>
            ))}
          </ColumnLayout>
        </Container>

        {/* Column Selection (only if Threat Catalog is selected) */}
        {sections.threatCatalog && (
          <>
            <Container
              header={
                <Header
                  variant="h2"
                  description={`${selectedColumnsCount} of ${Object.keys(columns).length} columns selected`}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={selectAllColumns} variant="inline-link">
                        Select All
                      </Button>
                      <Button onClick={deselectAllColumns} variant="inline-link">
                        Deselect All
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Threat Catalog Columns
                </Header>
              }
            >
              <ColumnLayout columns={2} variant="text-grid">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <Checkbox
                    key={key}
                    checked={columns[key]}
                    onChange={() => toggleColumn(key)}
                  >
                    {label}
                  </Checkbox>
                ))}
              </ColumnLayout>
            </Container>

            {/* Risk Score Filter */}
            <Container
              header={
                <Header
                  variant="h2"
                  description={`${selectedRiskScoresCount} of ${Object.keys(riskScoreFilter).length} risk levels selected`}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={selectAllRiskScores} variant="inline-link">
                        Select All
                      </Button>
                      <Button onClick={deselectAllRiskScores} variant="inline-link">
                        Deselect All
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Filter by Risk Score
                </Header>
              }
            >
              <ColumnLayout columns={3} variant="text-grid">
                {Object.keys(riskScoreFilter).map((riskLevel) => (
                  <Checkbox
                    key={riskLevel}
                    checked={riskScoreFilter[riskLevel]}
                    onChange={() => toggleRiskScore(riskLevel)}
                  >
                    {riskLevel}
                  </Checkbox>
                ))}
              </ColumnLayout>
            </Container>
          </>
        )}

        {/* Warning if no sections selected */}
        {selectedSectionsCount === 0 && (
          <Box variant="p" color="text-status-error">
            Please select at least one section to export.
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
};
